import { dash } from "@dropbox/api-v2-client";
import { userInfoMapAtom } from "@mirage/mosaics/UserProfilePage/atoms";
import { callApiV2 } from "@mirage/service-dbx-api";
import { useAtom } from "jotai";
import { useEffect } from "react";

import type {
  EmployeeType,
  UserInfo,
} from "@mirage/mosaics/UserProfilePage/types";

export const formatEmployeeType = (type: string): EmployeeType => {
  return ["contractor", "contingent"].includes(type.toLowerCase())
    ? "Contractor"
    : "Regular";
};

export const formatDashGetPersonResponse = (
  personResponse: dash.PersonWithFullMetadata | undefined,
): UserInfo | undefined => {
  if (
    !personResponse ||
    !personResponse.uuid ||
    !personResponse.account_id ||
    !personResponse.display_handle ||
    !personResponse.email ||
    !personResponse.name
  ) {
    return undefined;
  }

  return {
    uuid: personResponse.uuid,
    account_id: personResponse.account_id,
    display_handle: personResponse.display_handle,
    name: personResponse.name,
    email: personResponse.email,
    profile_photo_url: personResponse.profile_photo_url ?? null,
    role: personResponse.role ?? null,
    level: personResponse.level ?? null,
    is_deactivated: personResponse.is_deactivated ?? false,
    tenure: personResponse.tenure_undefined
      ? null
      : (personResponse.tenure ?? null),
    tenure_percentile: personResponse.tenure_percentile_undefined
      ? null
      : (personResponse.tenure_percentile ?? null),
    birthday: personResponse.birthday ?? null,
    city: personResponse.city ?? null,
    hire_date: personResponse.hire_date ?? null,
    location: personResponse.location ?? null,
    manager_uuid: personResponse.manager_uuid ?? null,
    on_leave: personResponse.on_leave ?? false,
    show_birthday: false,
    timezone_offset: personResponse.timezone_offset_undefined
      ? null
      : (personResponse.timezone_offset ?? null),
    type: formatEmployeeType(personResponse.type ?? ""),
    upcoming_pto_list: personResponse.upcoming_pto?.length
      ? personResponse.upcoming_pto.map((pto) => ({
          start_date: pto.start_date ?? null,
          end_date: pto.end_date ?? null,
        }))
      : null,
    team_names: personResponse.teams ?? [],
    slack_message_link: personResponse.slack_message_link ?? null,
    google_calendar_link: personResponse.google_calendar_link ?? null,
    user_profile_settings: personResponse.user_profile_settings ?? {},
  };
};

export function useUserProfileApi({
  displayHandle,
}: {
  displayHandle: string;
}): UserInfo | null | undefined {
  const [userInfoMap, setUserInfoMap] = useAtom(userInfoMapAtom);

  useEffect(() => {
    async function fetchUserInfo() {
      if (!displayHandle || userInfoMap[displayHandle] !== undefined) {
        return;
      }

      callApiV2("dashPeopleGetPerson", {
        display_handle: displayHandle,
      })
        .then((response) => {
          const formattedResponse = formatDashGetPersonResponse(
            response.person,
          );
          setUserInfoMap((prev) => ({
            ...prev,
            [displayHandle]: formattedResponse ?? null,
          }));
        })
        .catch(() => {
          // If user is not found, set the user info to null
          setUserInfoMap((prev) => ({
            ...prev,
            [displayHandle]: null,
          }));
        });
    }

    fetchUserInfo();
  }, [displayHandle]);

  if (displayHandle === undefined) {
    return null;
  }
  return userInfoMap[displayHandle];
}
